import { BrowserRouter as Router, Routes, Route, Navigate, Link, useNavigate, useLocation } from 'react-router-dom';
import { Header } from './components/Header';
import { Sidebar } from './components/Sidebar';
import { Footer } from './components/Footer';
import { ProcessingOverlay } from './components/ProcessingOverlay';
import { Login } from './pages/Login';
import { Register } from './pages/Register';
import { ChangePassword } from './pages/ChangePassword';
import { ForgotPassword } from './pages/ForgotPassword';
import { ResetPassword } from './pages/ResetPassword';
import { Dashboard } from './pages/Dashboard';
import { LandingPage } from './pages/LandingPage';
import { Blog } from './pages/Blog';
import { BlogPost } from './pages/BlogPost';
import { About } from './pages/About';
import { Contact } from './pages/Contact';
import { Terms } from './pages/Terms';
import { CreditHistory } from './pages/CreditHistory';
import { MyDrafts } from './pages/MyDrafts';
import { AdminDashboard } from './pages/admin/AdminDashboard';
import { AdminOverview } from './pages/admin/AdminOverview';
import { UserManagement } from './pages/admin/UserManagement';
import { CreditManagement } from './pages/admin/CreditManagement';
import { InviteManagement } from './pages/admin/InviteManagement';
import { AdminLogs } from './pages/admin/AdminLogs';
import { BlogManagement } from './pages/admin/BlogManagement';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ProcessingProvider, useProcessing } from './context/ProcessingContext';
import { ToastProvider } from './context/ToastContext';
import { FavoritesProvider } from './context/FavoritesContext';
import { useState, useEffect, useRef } from 'react';
import { LatestBlogPost } from './components/LatestBlogPost';
import { AdminChangePassword } from './components/AdminChangePassword';
import { VerifyEmail } from './pages/VerifyEmail';
import { SetPassword } from './pages/SetPassword';
import { AUTH_ENABLED } from './config/features';

import { LeaseAbstract } from './pages/leasing/LeaseAbstract';
import { BaseRentSensitivity } from './pages/leasing/BaseRentSens';
import { LoIAnalysis } from './pages/leasing/LoIAnalysis';
import { LeaseAnalysis } from './pages/leasing/LeaseAnalysis';

// Import PaperAssistant
import { PaperAssistant } from './pages/features/PaperAssistant';

// Import Translator
import { Translator } from './pages/Translator';

function PrivateRoute({ children }: { children: React.ReactNode }) {
  const { user, isLoading } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (user && !isLoading) {
      if (!user.has_password && location.pathname !== '/set-password') {
        navigate('/set-password');
      }
    }
  }, [user, isLoading, location.pathname, navigate]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[--bg-secondary]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return user ? children : <Navigate to={AUTH_ENABLED ? "/login" : "/"} />;
}

function AdminRoute({ children }: { children: React.ReactNode }) {
  const { user, isLoading, isAdmin } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[--bg-secondary]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!user || !isAdmin()) {
    return <Navigate to="/" />;
  }

  return children;
}

function PublicRoute({ children }: { children: React.ReactNode }) {
  const { user, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[--bg-secondary]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return !user ? children : <Navigate to="/dashboard" />;
}

function MainContent() {
  const { user } = useAuth();
  const { isProcessing } = useProcessing();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  return (
    <div className="min-h-screen flex flex-col bg-[--bg-secondary]">
      <Header hasUnsavedChanges={hasUnsavedChanges} />
      <div className="flex-1 flex flex-col min-h-0">
        {user && (
          <Sidebar
            isOpen={sidebarOpen}
            onToggle={() => setSidebarOpen(!sidebarOpen)}
            hasUnsavedChanges={hasUnsavedChanges}
          />
        )}
        <main className={`transition-all duration-300 flex-1 flex flex-col pt-16 min-h-0 ${user ? 'ml-16' : ''}`}>
          <div className="flex-1 flex flex-col">
            <Routes>
              <Route path="/login" element={
                AUTH_ENABLED ? (
                  <PublicRoute>
                    <Login />
                  </PublicRoute>
                ) : (
                  <Navigate to="/" replace />
                )
              } />
              <Route path="/register" element={
                AUTH_ENABLED ? (
                  <PublicRoute>
                    <Register />
                  </PublicRoute>
                ) : (
                  <Navigate to="/" replace />
                )
              } />
              <Route path="/verify-email" element={
                AUTH_ENABLED ? (
                  <PublicRoute>
                    <VerifyEmail />
                  </PublicRoute>
                ) : (
                  <Navigate to="/" replace />
                )
              } />
              <Route path="/forgot-password" element={
                AUTH_ENABLED ? (
                  <PublicRoute>
                    <ForgotPassword />
                  </PublicRoute>
                ) : (
                  <Navigate to="/" replace />
                )
              } />
              <Route path="/set-password" element={
                <PrivateRoute>
                  <SetPassword />
                </PrivateRoute>
              } />

              {/* Blog Routes */}
              <Route path="/blog" element={<Blog />} />
              <Route path="/blog/:slug" element={<BlogPost />} />

              {/* About, Contact, and Terms Routes */}
              <Route path="/about" element={<About />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/terms" element={<Terms />} />

              {/* Account Management Routes */}
              <Route path="/change-password" element={
                <PrivateRoute>
                  <ChangePassword />
                </PrivateRoute>
              } />

              {/* Regular user routes */}
              <Route path="/dashboard" element={
                <PrivateRoute>
                  <Dashboard />
                </PrivateRoute>
              } />
              <Route path="/credit-history" element={
                <PrivateRoute>
                  <CreditHistory setHasUnsavedChanges={setHasUnsavedChanges} />
                </PrivateRoute>
              } />
              <Route path="/my-drafts" element={
                <PrivateRoute>
                  <MyDrafts />
                </PrivateRoute>
              } />

              {/* PaperAssistant Route */}
              <Route path="/paper-assistant" element={
                <PrivateRoute>
                  <PaperAssistant />
                </PrivateRoute>
              } />

              {/* Translator Route */}
              <Route path="/translator" element={
                <PrivateRoute>
                  <Translator />
                </PrivateRoute>
              } />

              {/* Leasing Routes */}
              <Route path="/lease-analysis" element={
                <PrivateRoute>
                  <LeaseAnalysis />
                </PrivateRoute>
              } />

              <Route path="/lease-abstract" element={
                <PrivateRoute>
                  <LeaseAbstract />
                </PrivateRoute>
              } />

              <Route path="/base-rent-sens" element={
                <PrivateRoute>
                  <BaseRentSensitivity />
                </PrivateRoute>
              } />

              <Route path="/loi-analysis" element={
                <PrivateRoute>
                  <LoIAnalysis />
                </PrivateRoute>
              } />

              <Route path="/" element={
                user ? (
                  <Navigate to="/dashboard" replace />
                ) : (
                  <LandingPage />
                )
              } />
            </Routes>
          </div>
        </main>
      </div>
      <Footer />
    </div>
  );
}

function AdminContent() {
  return (
    <Routes>
      <Route path="/admin" element={
        <AdminRoute>
          <AdminDashboard />
        </AdminRoute>
      }>
        <Route index element={<AdminOverview />} />
        <Route path="users" element={<UserManagement />} />
        <Route path="credits" element={<CreditManagement />} />
        <Route path="invites" element={<InviteManagement />} />
        <Route path="logs" element={<AdminLogs />} />
        <Route path="blog" element={<BlogManagement />} />
        <Route path="password" element={<AdminChangePassword />} />
      </Route>
      <Route path="*" element={<Navigate to="/admin" replace />} />
    </Routes>
  );
}

function AppContent() {
  const { user, isAdmin } = useAuth();
  const location = useLocation();

  // If we're on the reset-password route, render it outside of MainContent
  if (location.pathname === '/reset-password') {
    return <ResetPassword />;
  }

  return isAdmin() ? <AdminContent /> : <MainContent />;
}

function App() {
  return (
    <AuthProvider>
      <Router>
        <ProcessingProvider>
          <ToastProvider>
            <FavoritesProvider>
              <ProcessingOverlay />
              <AppContent />
            </FavoritesProvider>
          </ToastProvider>
        </ProcessingProvider>
      </Router>
    </AuthProvider>
  );
}

export default App;